import { useEffect, useState } from "react";
import { getNews, type MarketNews } from "@/api/market";

function formatTime(timestamp: number): string {
  const date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
  const diffMs = Date.now() - date.getTime();
  const diffHours = diffMs / 3_600_000;
  if (diffHours < 1) return `${Math.max(1, Math.round(diffMs / 60_000))}分钟前`;
  if (diffHours < 24) return `${Math.round(diffHours)}小时前`;
  return date.toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" });
}

const importanceStyle: Record<string, { color: string; label: string }> = {
  critical: { color: "bg-red-500", label: "重磅" },
  high: { color: "bg-orange-500", label: "重要" },
  normal: { color: "bg-blue-400", label: "一般" },
  low: { color: "bg-gray-300", label: "低" },
};

export default function MarketNewsSection() {
  const [news, setNews] = useState<MarketNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    getNews(undefined, 10)
      .then((data) => {
        if (active) setNews(data?.news || []);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  return (
    <section className="px-4 mt-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-foreground-950">市场资讯</span>
        {!loading && news.length > 0 && (
          <a href="/news" className="text-xs text-primary-500 hover:text-primary-600">
            查看更多 <i className="ri-arrow-right-s-line" />
          </a>
        )}
      </div>

      <div className="bg-white rounded-xl overflow-hidden border border-background-200/60">
        {loading && [1, 2, 3].map((item) => (
          <div key={item} className="px-4 py-3 border-b border-background-100 animate-pulse">
            <div className="h-3 bg-background-100 rounded w-1/4 mb-2" />
            <div className="h-4 bg-background-100 rounded w-3/4 mb-1" />
            <div className="h-3 bg-background-100 rounded w-1/2" />
          </div>
        ))}

        {!loading && news.length === 0 && (
          <div className="p-5 text-center">
            <i className="ri-news-line text-lg text-foreground-300" />
            <p className="text-xs text-foreground-400 mt-1">
              {failed ? "资讯加载失败，请稍后重试" : "暂无市场资讯"}
            </p>
          </div>
        )}

        {!loading && news.slice(0, 5).map((item) => {
          const style = importanceStyle[item.importance];
          const content = (
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  {style && (
                    <span className={`text-[8px] text-white px-1 py-0.5 rounded-full ${style.color}`}>
                      {style.label}
                    </span>
                  )}
                  <span className="text-[10px] text-foreground-400 truncate">{item.source}</span>
                </div>
                <p className="text-xs font-medium text-foreground-950 leading-snug line-clamp-2">{item.title}</p>
                {item.summary && <p className="text-[10px] text-foreground-500 mt-0.5 line-clamp-1">{item.summary}</p>}
              </div>
              <span className="text-[9px] text-foreground-400 shrink-0 mt-0.5">
                {formatTime(item.publishedAt || item.createdAt)}
              </span>
            </div>
          );

          return item.url ? (
            <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer"
              className="block px-4 py-3 border-b border-background-100 last:border-b-0 hover:bg-background-50 transition-colors">
              {content}
            </a>
          ) : (
            <div key={item.id} className="px-4 py-3 border-b border-background-100 last:border-b-0">
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}
